/**
 * TypeScript Type Definitions — updated with column detection types.
 */

export type PhoneCategory =
  | 'GOOD'
  | 'VOICEMAIL'
  | 'SWITCHBOARD'
  | 'FAX'
  | 'DEAD'
  | 'NO_ANSWER'
  | 'BUSY'
  | 'UNKNOWN';

export type JobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type UserRole = 'admin' | 'user';

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  email_verified?: boolean;
  created_at: string;
  last_login?: string | null;
  last_login_tz?: string | null;
  deleted_at?: string | null;
  run_count?: number | null;
}

export interface AdminCreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

export interface AdminUpdateUserRequest {
  is_active?: boolean;
  role?: UserRole;
  username?: string;
  email?: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SignupRequest {
  username: string;
  email: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  user: User;
}

// ── Billing (billing-credits-01) ───────────────────────────────────────────────

export interface BillingPlan {
  tier: string;
  included_dials: number;
  price_display: string;
  price_id: string | null;
}

export interface SubscriptionInfo {
  enforced: boolean;
  tier: string | null;
  status: string | null;
  included_dials: number | null;
  used: number | null;
  remaining: number | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

// ── CCK free-credit / coupon system (cck-credits-01) ───────────────────────────

export interface CckGrant {
  amount: number;
  remaining: number;
  expires_at: string | null;
  source: string; // "coupon" | "admin"
}

export interface CckCreditsMe {
  enabled: boolean;
  balance: number;
  total_granted: number;
  total_used: number;
  jobs: number;
  uploads: number;
  soonest_expires_at: string | null;
  expiring_soon: boolean;
  grants: CckGrant[];
}

export interface CckCreditSummary {
  metered: boolean;
  total_remaining: number;
  cck: {
    enabled: boolean;
    balance: number;
    total_granted: number;
    can_redeem: boolean;
    soonest_expires_at: string | null;
    expiring_soon: boolean;
  };
  stripe: {
    tier: string | null;
    status: string | null;
    included_dials: number | null;
    used: number | null;
    remaining: number | null;
    spendable: boolean;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
  } | null;
}

export interface CckRedeemResult {
  balance: number;
  credits_granted: number;
  expires_at: string | null;
}

export interface CckAdminUser {
  user_id: string;
  email: string;
  username: string;
  balance: number;
  used: number;
  jobs: number;
  uploads: number;
  soonest_expires_at: string | null;
  cck_credit_exempt: boolean;
}

export interface CckCode {
  id: string;
  code: string;
  credit_value: number;
  validity_days: number;
  max_redemptions: number | null;
  times_redeemed: number;
  active: boolean;
  code_expires_at: string | null;
  description: string | null;
  created_at: string | null;
}

export interface CckRedemptionHistory {
  code: string;
  user_id: string;
  credits_granted: number;
  redeemed_at: string | null;
}

export interface CckLedgerEntry {
  reason: string; // coupon_grant | admin_grant | job_reserve | job_refund | expiry
  delta: number;  // + grant/refund, − reserve/expiry
  job_id: string | null;
  created_at: string | null;
}

// ── Column detection ──────────────────────────────────────────────────────────

export interface ColumnInfo {
  name: string;
  display_name: string;
  sample_values: string[];
  detection_method: 'header' | 'unnamed_adjacent' | 'content' | 'gemini';
  has_suffixes: boolean;
  has_multi_number: boolean;
  estimated_count: number;
}

export interface SheetColumnInfo {
  sheet_name: string;
  all_columns: string[];
  detected_columns: ColumnInfo[];
  row_count: number;
  header_row: number;
  header_confidence: 'high' | 'medium' | 'low';
  header_candidates?: { row: number; score: number }[];
  sample_rows?: Record<string, string>[];
  /** {column: how many of its cells the dialer could actually call} */
  dialable_counts?: Record<string, number>;
}

export interface ColumnPreviewResponse {
  upload_id: string;
  sheets: SheetColumnInfo[];
  gemini_used: boolean;
  explanation: string;
}

export interface ConfirmColumnsResponse {
  confirmed: boolean;
  total_numbers: number;
}

// ── Upload ────────────────────────────────────────────────────────────────────

export interface SheetInfo {
  name: string;
  row_count: number;
  phone_columns: string[];
  header_row: number;
  header_confidence?: 'high' | 'medium' | 'low';
  header_candidates?: { row: number; score: number }[];
  preview_data?: Record<string, any>[];
}

// ── Google Sheets input (gsheet-io-01) ───────────────────────────────────────

export interface GSheetTab {
  gid: number;
  title: string;
  index: number;
  row_count: number;
  column_count: number;
  header: string[];
  header_row: number;
  header_confidence: 'high' | 'medium' | 'low';
  header_candidates?: { row: number; score: number }[];
  likely_phone_columns: string[];
  /** False for reference tabs (Search Criteria, DNC, …) — cannot be dialled. */
  has_phone_data: boolean;
  sampled_rows: number;
}

export interface GSheetServiceAccount {
  email: string;
  role_needed: string;
  note: string;
}

export interface GSheetInspectResponse {
  spreadsheet_id: string;
  title: string;
  tabs: GSheetTab[];
}

export interface WritebackStatus {
  job_id: string;
  /** none | pending | partial | done | dry_run | failed */
  status: string;
  cells: number;
  error: string | null;
  at: string | null;
  blocks: Record<string, { gid: number; title: string; first_col: number; headers: string[]; columns: string[] }>;
  dry_run: boolean;
}

export interface UploadResponse {
  upload_id: string;
  filename: string;
  sheets: SheetInfo[];
  total_rows: number;
  message: string;
}

// ── Verification ──────────────────────────────────────────────────────────────

export interface PhoneNumberResult {
  phone_number: string;
  category: PhoneCategory;
  raw_category?: string;
  call_sid?: string;
  processed_at: string;
  duration_seconds?: number;
  notes?: string;
}

export interface VerifyStartRequest {
  upload_id: string;
  selected_sheets: string[];
  column_map?: Record<string, string[]>;
  row_ranges?: RowRangesBySheet;
  header_rows?: Record<string, number>;
  concurrent_calls?: number;
}

export interface RowRange {
  start_row: number;
  end_row: number;
}

export type RowRangesBySheet = Record<string, RowRange[]>;

export interface VerifyStartResponse {
  job_id: string;
  status: JobStatus;
  total_numbers: number;
  message: string;
}

export interface ProgressUpdate {
  job_id: string;
  current: number;
  total: number;
  percentage: number;
  call_sid?: string;
  current_number?: string;
  current_status?: string;
  category_counts: Record<string, number>;
  estimated_remaining_seconds?: number;
}

export interface JobStatusResponse {
  job_id: string;
  status: JobStatus;
  total_numbers: number;
  processed_count: number;
  percentage: number;
  category_counts: Record<string, number>;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  export_ready: boolean;
}

export interface AnalysisResult {
  phone_number: string;
  call_sid: string;
  initial_category: string;
  final_category: string;
  analysis_status: 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'no_recording' | 'skipped';
  ai_confidence: number | null;
  ai_notes: string | null;
  notes: string | null;
  recording_available: boolean;
  has_transcription: boolean;
  owner_verified: boolean | null;
  owner_name: string | null;
  processed_at: string;
}

export interface AnalysisResponse {
  job_id: string;
  summary?: {
    job_status: string;
    health: 'working' | 'blocked' | 'complete';
    counts: {
      total: number;
      completed: number;
      failed: number;
      queued: number;
      processing: number;
      no_recording: number;
      other: number;
    };
    unanalysed: number;
    writeback_status: string;
    writeback_error: string | null;
    user_message: string;
  };
  results: AnalysisResult[];
}

export interface AllCallsResponse {
  job_id: string;
  calls: CallRecord[];
}

export interface CallRecord {
  id: string;
  job_id: string;
  phone_number: string;
  raw_value: string | null;
  call_sid: string | null;
  category: string;
  initial_category: string;
  raw_amd: string | null;
  duration_seconds: number | null;
  analysis_status: string;
  ai_confidence: number | null;
  ai_notes: string | null;
  source_sheet: string | null;
  source_column: string | null;
  recording_downloaded: boolean;
  has_transcription: boolean;
  processed_at: string;
}

export interface CallLogEntry {
  type: 'call_started' | 'call_completed' | 'analysis_update';
  call_sid: string | null;
  phone_number: string;
  category?: string;
  analysis_status?: string;
  timestamp: string;
  ai_queued?: boolean;
}

export interface HistoryJob {
  job_id: string;
  filename: string;
  status: JobStatus;
  total_numbers: number;
  processed_count: number;
  category_counts: Record<string, number>;
  created_at: string;
  completed_at?: string;
  export_ready: boolean;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export const CATEGORY_COLORS: Record<PhoneCategory, string> = {
  GOOD: 'var(--category-good-bg)',
  VOICEMAIL: 'var(--category-voicemail-bg)',
  SWITCHBOARD: 'var(--category-switchboard-bg)',
  FAX: 'var(--category-fax-bg)',
  DEAD: 'var(--category-dead-bg)',
  NO_ANSWER: 'var(--category-no-answer-bg)',
  BUSY: 'var(--category-busy-bg)',
  UNKNOWN: 'var(--category-unknown-bg)',
};

export const CATEGORY_TEXT_COLORS: Record<PhoneCategory, string> = {
  GOOD: 'var(--category-good-fg)',
  VOICEMAIL: 'var(--category-voicemail-fg)',
  SWITCHBOARD: 'var(--category-switchboard-fg)',
  FAX: 'var(--category-fax-fg)',
  DEAD: 'var(--category-dead-fg)',
  NO_ANSWER: 'var(--category-no-answer-fg)',
  BUSY: 'var(--category-busy-fg)',
  UNKNOWN: 'var(--category-unknown-fg)',
};

export const CATEGORY_LABELS: Record<PhoneCategory, string> = {
  GOOD: 'Good (Human)',
  VOICEMAIL: 'Voicemail',
  SWITCHBOARD: 'Switchboard / IVR',
  FAX: 'Fax Machine',
  DEAD: 'Dead / Disconnected',
  NO_ANSWER: 'No Answer',
  BUSY: 'Busy',
  UNKNOWN: 'Unknown',
};

export const DETECTION_METHOD_LABELS: Record<string, string> = {
  header: 'Header match',
  unnamed_adjacent: 'Adjacent to phone column',
  content: 'Content analysis',
  dialable_content: 'Dialable numbers found',
  gemini: 'AI detected',
};

// ── Insights (insights-demo-01) ─────────────────────────────────────────────
// GET /api/insights — one account's totals. Aggregates only: never a name,
// phone number or file name.

export type InsightsRange = '30d' | '90d' | 'all';

export interface InsightsKpis {
  numbers_verified: number;
  live: number;
  live_pct: number;
  reached: number;
  reached_pct: number;
  humans: number;
  humans_pct: number;
  owners_confirmed: number;
  owners_pct: number;
  bad_removed: number;
  bad_pct: number;
  hours_saved: number;
  work_weeks: number;
  manual_seconds_per_dial: number;
}

export interface InsightsFunnelStep {
  key: string;
  label: string;
  count: number;
  pct: number;
}

export interface InsightsNumberType {
  group: string;
  count: number;
  share_pct: number;
  human_pct: number;
  owner_pct: number;
  bad_pct: number;
}

export interface InsightsAiCaught {
  total: number;
  pct: number;
  by_category: Partial<Record<PhoneCategory, number>>;
}

export interface InsightsWeek {
  week_start: string;
  human: number;
  voicemail: number;
  switchboard: number;
  dead: number;
  other: number;
  total: number;
  hours_saved_cumulative: number;
}

export interface InsightsResponse {
  range: InsightsRange;
  generated_at: string;
  has_data: boolean;
  grade: string | null;
  kpis: InsightsKpis;
  funnel: InsightsFunnelStep[];
  number_types: InsightsNumberType[];
  ai_caught: InsightsAiCaught;
  weekly: InsightsWeek[];
}
